import apiClient from '@/lib/apiClient';
import { ApiResponse, Order, PaginatedResponse } from '@/types/api';

/**
 * Order API endpoints
 */
export const orderApi = {
  /**
   * Get all orders with pagination and filters
   */
  getOrders: async (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Order>> => {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Order>>>(
      '/orders',
      {
        params,
      }
    );
    return response.data.data!;
  },

  /**
   * Get single order by ID
   */
  getOrder: async (id: string): Promise<Order> => {
    const response = await apiClient.get<ApiResponse<Order>>(`/orders/${id}`);
    return response.data.data!;
  },

  /**
   * Update order status
   */
  updateOrderStatus: async (
    id: string,
    status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  ): Promise<Order> => {
    const response = await apiClient.patch<ApiResponse<Order>>(
      `/orders/${id}/status`,
      {
        status,
      }
    );
    return response.data.data!;
  },

  /**
   * Update order fields (status, tracking, address, notes, etc.)
   */
  updateOrder: async (
    id: string,
    data: Record<string, unknown>
  ): Promise<Order> => {
    const response = await apiClient.patch<ApiResponse<Order>>(
      `/orders/${id}`,
      data
    );
    return response.data.data!;
  },

  /**
   * Cancel order
   */
  cancelOrder: async (id: string, reason?: string): Promise<Order> => {
    const response = await apiClient.post<ApiResponse<Order>>(
      `/orders/${id}/cancel`,
      {
        reason,
      }
    );
    return response.data.data!;
  },

  /**
   * Get order tracking information
   */
  getTracking: async (
    id: string
  ): Promise<{
    trackingNumber?: string;
    carrier?: string;
    status: string;
    events: Array<{ timestamp: string; status: string; location?: string }>;
  }> => {
    const response = await apiClient.get<
      ApiResponse<{
        trackingNumber?: string;
        carrier?: string;
        status: string;
        events: Array<{ timestamp: string; status: string; location?: string }>;
      }>
    >(`/orders/${id}/tracking`);
    return response.data.data!;
  },
};
